import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import {
  Sun,
  Zap,
  Battery,
  Building2,
  MapPin,
  DollarSign,
  Users,
  HardHat,
  CheckCircle,
  ArrowRight,
  ChevronDown,
  ChevronUp,
  TrendingDown,
  Home,
  Factory,
  Wrench,
  Shield,
  Globe,
} from "lucide-react";

/* ── Animated counter ── */
const AnimatedCounter = ({ end, prefix = "", suffix = "", duration = 2000 }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started.current) {
          started.current = true;
          const startTime = Date.now();
          const numEnd = parseFloat(String(end).replace(/[^0-9.]/g, ""));
          const tick = () => {
            const elapsed = Date.now() - startTime;
            const progress = Math.min(elapsed / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3);
            setCount(Math.floor(eased * numEnd));
            if (progress < 1) requestAnimationFrame(tick);
            else setCount(numEnd);
          };
          requestAnimationFrame(tick);
        }
      },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [end, duration]);

  return (
    <span ref={ref}>
      {prefix}
      {count.toLocaleString()}
      {suffix}
    </span>
  );
};

/* ── FAQ Item ── */
const FAQItem = ({ question, answer }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-slate-200 bg-white">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-5 text-left hover:bg-slate-50 transition-colors"
      >
        <span className="font-bold text-slate-900 pr-4">{question}</span>
        {open ? (
          <ChevronUp className="w-5 h-5 text-[#002B7F] flex-shrink-0" />
        ) : (
          <ChevronDown className="w-5 h-5 text-slate-400 flex-shrink-0" />
        )}
      </button>
      {open && (
        <div className="px-5 pb-5">
          <p className="text-slate-600 leading-relaxed">{answer}</p>
        </div>
      )}
    </div>
  );
};

const solarSites = [
  { region: "Las Vegas Region", count: 2, desc: "Powering Southern Nevada's largest metro area" },
  { region: "Reno / Sparks", count: 2, desc: "Clean energy for Northern Nevada communities" },
  { region: "Elko / Wells", count: 1, desc: "Rural energy independence in northeast Nevada" },
  { region: "Pahrump / Indian Springs / Beatty", count: 1, desc: "Ideal desert solar conditions" },
  { region: "Fallon / Winnemucca Corridor", count: 2, desc: "Central Nevada energy backbone" },
  { region: "Tonopah / Ely Region", count: 2, desc: "Remote communities gain local power generation" },
];

const jobCategories = [
  { icon: HardHat, title: "Construction Labor", desc: "Heavy equipment, grading, foundation work across all 10 sites" },
  { icon: Zap, title: "Electrical Workers", desc: "Panel installation, inverter systems, grid interconnection" },
  { icon: Wrench, title: "Skilled Trades", desc: "Welding, plumbing, structural work for facilities" },
  { icon: Factory, title: "Maintenance Technicians", desc: "Long-term operations, panel cleaning, battery monitoring" },
];

const faqs = [
  {
    question: "Will this replace NV Energy completely?",
    answer: "This plan does not claim to replace the entire grid overnight. It builds state-owned generation capacity that reduces dependence on external utility pricing over time. The existing grid infrastructure will continue to operate alongside state-owned solar sites.",
  },
  {
    question: "How is this paid for?",
    answer: "Through the Nevada Clean Energy Fund, supported by federal DOE Title 17 loan programs, state revenue bonds, and redirected energy subsidies. The total cost of $11-14 billion is spread over a 20-year financing horizon at approximately $1.0-1.2 billion per year — less than what Nevadans currently pay in rising utility costs.",
  },
  {
    question: "Are all the jobs permanent?",
    answer: "The 100,000+ construction-phase jobs are tied to the multi-year buildout period. However, thousands of long-term permanent positions in operations, maintenance, and energy management will be created. Local hiring priority ensures these jobs benefit Nevada communities.",
  },
  {
    question: "What happens at night when there's no sun?",
    answer: "Every solar site is paired with large-scale battery storage systems. Excess energy generated during peak sunlight hours is stored and delivered at night and during peak demand periods. This ensures 24/7 reliable power.",
  },
  {
    question: "Will this actually lower my electricity bill?",
    answer: "The target is approximately a 39% reduction in electricity costs. For an average household using 900 kWh/month, this means going from approximately $126/month to roughly $76.50/month. Final rates may vary based on market conditions and buildout timing.",
  },
  {
    question: "Why state-owned instead of private?",
    answer: "When the state owns the generation and storage assets, electricity is sold at cost — not for profit. After the financing period, costs drop further because there are no ongoing shareholder obligations. Revenue is reinvested into infrastructure, not extracted as profit.",
  },
];

const CleanEnergyPage = () => {
  return (
    <div data-testid="clean-energy-page" className="pt-16 md:pt-20">
      {/* ── HERO ── */}
      <section className="relative py-16 md:py-24 bg-slate-900">
        <div className="absolute inset-0 bg-[#002B7F]/90" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#D4AF37] mb-4">
            Clean Energy & Jobs
          </p>
          <h1 className="text-4xl sm:text-5xl font-black tracking-tighter text-white mb-4 max-w-3xl">
            Powering Nevada's Future
          </h1>
          <p className="text-lg text-white/80 max-w-2xl mb-8">
            A state-owned solar and battery storage network that lowers
            electricity costs, creates jobs, and keeps energy affordable for
            every Nevadan.
          </p>

          {/* Stat Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {[
              { icon: Zap, stat: "~39%", label: "Lower electricity costs" },
              { icon: HardHat, stat: "100,000+", label: "Construction-phase jobs" },
              { icon: Sun, stat: "10", label: "Solar + storage sites statewide" },
              { icon: Building2, stat: "State-Owned", label: "Energy system for NV families" },
            ].map((card) => {
              const Icon = card.icon;
              return (
                <div key={card.label} className="bg-white/10 border border-white/20 p-5">
                  <Icon className="w-5 h-5 text-[#D4AF37] mb-2" />
                  <p className="text-2xl font-black text-white">{card.stat}</p>
                  <p className="text-xs text-white/70">{card.label}</p>
                </div>
              );
            })}
          </div>

          <div className="flex flex-wrap gap-4">
            <a
              href="#the-plan"
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#D4AF37] hover:bg-[#B5952F] text-black font-bold uppercase tracking-widest text-sm transition-all hover:-translate-y-1 hover:shadow-xl"
            >
              Explore the Plan
            </a>
            <a
              href="#cost-breakdown"
              className="inline-flex items-center gap-2 px-8 py-4 border-2 border-white text-white hover:bg-white hover:text-[#002B7F] font-bold uppercase tracking-widest text-sm transition-all"
            >
              See Cost Breakdown
            </a>
          </div>

          {/* Video Overview */}
          <div className="mt-10 max-w-2xl">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#D4AF37] mb-3">Watch the Overview</p>
            <div className="relative aspect-video bg-black border border-white/20 overflow-hidden">
              <iframe
                src="https://www.youtube.com/embed/Gx-edEHuWI0?rel=0&modestbranding=1"
                title="Lowering Energy Costs - Cooper for Nevada"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="absolute inset-0 w-full h-full"
                data-testid="clean-energy-video"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ── THE PROBLEM ── */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6">
              Nevada's Energy Costs Are Too High
            </h2>
            <p className="text-slate-600 leading-relaxed mb-6">
              Nevada families are paying more for electricity every year, with
              limited control over what they're charged. Utility pricing models
              are designed to generate profit — not to serve communities. Rates
              go up, and families have no alternative.
            </p>
            <p className="text-slate-600 leading-relaxed mb-8">
              Meanwhile, Nevada sits on one of the most powerful solar resources
              on the planet — and we're barely using it for the people who live
              here.
            </p>

            {/* Rising costs visual */}
            <div className="grid sm:grid-cols-3 gap-4 mb-8">
              <div className="bg-red-50 border border-red-200 p-5 text-center">
                <p className="text-3xl font-black text-red-600">~14¢</p>
                <p className="text-sm text-red-700">Current average cost per kWh</p>
              </div>
              <div className="bg-red-50 border border-red-200 p-5 text-center">
                <TrendingDown className="w-5 h-5 text-red-500 mx-auto mb-1 rotate-180" />
                <p className="text-sm text-red-700 font-bold">Costs rising every year</p>
              </div>
              <div className="bg-red-50 border border-red-200 p-5 text-center">
                <p className="text-3xl font-black text-red-600">$126</p>
                <p className="text-sm text-red-700">Average monthly bill (900 kWh)</p>
              </div>
            </div>

            <div className="bg-[#002B7F]/10 border border-[#002B7F]/20 p-5">
              <p className="text-[#002B7F] font-bold text-sm">
                Nevada should not be dependent on rising energy prices. We can
                produce our own power, for less.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── THE SOLUTION ── */}
      <section id="the-plan" className="py-16 md:py-24 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4">
              A State-Owned Clean Energy System
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Nevada will build 10 utility-scale solar farms paired with battery
              storage systems, distributed across the state.
            </p>
          </div>

          {/* 3-step diagram */}
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            {[
              {
                icon: Sun,
                step: "1",
                title: "Sunlight → Solar Panels",
                desc: "10 large-scale solar farms capture Nevada's abundant sunshine and convert it to electricity",
                color: "bg-amber-500",
              },
              {
                icon: Battery,
                step: "2",
                title: "Energy → Battery Storage",
                desc: "Excess energy stored in large battery systems for use at night and during peak demand",
                color: "bg-[#002B7F]",
              },
              {
                icon: Home,
                step: "3",
                title: "Power → Nevada Homes",
                desc: "Electricity delivered to homes and businesses at cost — no profit markup, no middleman",
                color: "bg-emerald-600",
              },
            ].map((item, i) => {
              const Icon = item.icon;
              return (
                <div key={item.title} className="relative">
                  <div className="bg-white border border-slate-200 p-8 text-center h-full">
                    <div className={`w-14 h-14 mx-auto flex items-center justify-center ${item.color} text-white mb-4`}>
                      <Icon className="w-7 h-7" />
                    </div>
                    <div className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">
                      Step {item.step}
                    </div>
                    <h3 className="text-lg font-bold text-slate-900 mb-3">
                      {item.title}
                    </h3>
                    <p className="text-sm text-slate-600">{item.desc}</p>
                  </div>
                  {i < 2 && (
                    <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-10">
                      <ArrowRight className="w-6 h-6 text-[#D4AF37]" />
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="bg-[#002B7F] p-6 text-center max-w-2xl mx-auto">
            <p className="text-white font-bold">
              Each site generates solar power during the day, stores excess
              energy in batteries, and delivers electricity at night and peak
              hours.
            </p>
          </div>
        </div>
      </section>

      {/* ── PROJECT SCALE / MAP ── */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4">
              Built for Nevada's Geography and Energy Needs
            </h2>
            <div className="flex flex-wrap justify-center gap-6 text-sm text-slate-600">
              <span>
                Nevada uses <strong className="text-slate-900">~105,000 MWh/day</strong>
              </span>
              <span>
                Plan builds <strong className="text-slate-900">10 large solar sites</strong>
              </span>
              <span>
                Uses <strong className="text-slate-900">less than 0.25%</strong> of Nevada land
              </span>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {solarSites.map((site) => (
              <div
                key={site.desc}
                className="bg-[#F8F9FA] border border-slate-200 p-5 hover:border-[#D4AF37] transition-colors"
              >
                <div className="flex items-center gap-3 mb-3">
                  <MapPin className="w-5 h-5 text-[#D4AF37]" />
                  <h3 className="font-bold text-slate-900 text-sm">
                    {site.region}
                  </h3>
                  <span className="ml-auto bg-[#002B7F] text-white text-xs font-bold px-2 py-1">
                    {site.count} {site.count > 1 ? "sites" : "site"}
                  </span>
                </div>
                <p className="text-xs text-slate-600">{site.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── COST & FINANCING ── */}
      <section id="cost-breakdown" className="py-16 md:py-24 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4">
              A Long-Term Investment in Nevada's Energy Independence
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <div className="bg-slate-900 p-8 text-center">
              <p className="text-4xl md:text-5xl font-black text-[#D4AF37] mb-2">
                <AnimatedCounter prefix="$" end={11} suffix="–14B" />
              </p>
              <p className="text-white/80 text-sm">Total investment</p>
            </div>
            <div className="bg-slate-900 p-8 text-center">
              <p className="text-4xl md:text-5xl font-black text-[#D4AF37] mb-2">
                20 yr
              </p>
              <p className="text-white/80 text-sm">Financing horizon</p>
            </div>
            <div className="bg-slate-900 p-8 text-center">
              <p className="text-4xl md:text-5xl font-black text-[#D4AF37] mb-2">
                ~$1.1B
              </p>
              <p className="text-white/80 text-sm">Annual cost (over 20 years)</p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white border border-slate-200 p-6">
              <h3 className="font-bold text-slate-900 mb-3">Financing Sources</h3>
              <ul className="space-y-2">
                {[
                  "Nevada Clean Energy Fund",
                  "Federal DOE Title 17 loan support",
                  "State revenue bonds",
                  "Redirected energy subsidies",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2 text-sm">
                    <CheckCircle className="w-4 h-4 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                    <span className="text-slate-600">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-[#002B7F]/10 border border-[#002B7F]/20 p-6 flex items-center">
              <p className="text-[#002B7F] font-bold text-sm leading-relaxed">
                This is not a short-term expense. It is long-term
                infrastructure ownership that replaces ongoing electricity
                payments to external providers.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── ELECTRICITY COST IMPACT ── */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4">
              Lower, More Stable Electricity Prices
            </h2>
          </div>

          <div className="max-w-3xl mx-auto">
            {/* Comparison Table */}
            <div className="border border-slate-200 overflow-hidden mb-8">
              <div className="grid grid-cols-2 bg-[#002B7F] text-white">
                <div className="p-4 font-bold text-sm border-r border-white/20">
                  System
                </div>
                <div className="p-4 font-bold text-sm">Cost per kWh</div>
              </div>
              <div className="grid grid-cols-2 border-b border-slate-200">
                <div className="p-4 text-sm text-slate-600 border-r border-slate-200 flex items-center gap-2">
                  <div className="w-2 h-2 bg-red-400 rounded-full" />
                  Current Nevada average
                </div>
                <div className="p-4 text-lg font-black text-red-600">~14¢</div>
              </div>
              <div className="grid grid-cols-2">
                <div className="p-4 text-sm text-slate-600 border-r border-slate-200 flex items-center gap-2">
                  <div className="w-2 h-2 bg-emerald-400 rounded-full" />
                  State solar + storage system
                </div>
                <div className="p-4 text-lg font-black text-emerald-600">
                  ~8.5¢
                </div>
              </div>
            </div>

            {/* Household Example */}
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div className="bg-red-50 border border-red-200 p-6 text-center">
                <p className="text-sm text-red-700 font-bold mb-2">Before</p>
                <p className="text-3xl font-black text-red-600">~$126/mo</p>
                <p className="text-xs text-red-600 mt-1">900 kWh at current rates</p>
              </div>
              <div className="bg-emerald-50 border border-emerald-200 p-6 text-center">
                <p className="text-sm text-emerald-700 font-bold mb-2">After</p>
                <p className="text-3xl font-black text-emerald-600">
                  ~$76.50/mo
                </p>
                <p className="text-xs text-emerald-600 mt-1">
                  900 kWh under state solar system
                </p>
              </div>
            </div>

            <div className="bg-slate-100 border border-slate-200 p-4 text-center">
              <p className="text-xs text-slate-500">
                Final rates may vary based on market conditions and buildout
                timing.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── JOBS & ECONOMIC IMPACT ── */}
      <section className="py-16 md:py-24 bg-[#002B7F]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              A Major Job Creation Program for Nevada
            </h2>
            <p className="text-white/70 max-w-2xl mx-auto">
              100,000+ construction-phase jobs plus thousands of long-term
              energy sector positions. Local hiring priority near project sites.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {jobCategories.map((item) => {
              const Icon = item.icon;
              return (
                <div key={item.title} className="bg-white/10 border border-white/20 p-6">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#D4AF37] text-black mb-4">
                    <Icon className="w-5 h-5" />
                  </div>
                  <h3 className="font-bold text-white mb-2 text-sm">
                    {item.title}
                  </h3>
                  <p className="text-white/70 text-xs">{item.desc}</p>
                </div>
              );
            })}
          </div>

          <div className="mt-8 bg-white/10 border border-white/20 p-5 text-center max-w-2xl mx-auto">
            <p className="text-[#D4AF37] font-bold">
              Nevada should not be dependent on rising energy prices. We can
              produce our own power, for less — and create massive job growth
              doing it.
            </p>
          </div>
        </div>
      </section>

      {/* ── BATTERY STORAGE EXPLAINER ── */}
      <section className="py-16 md:py-24 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4">
              Why Battery Storage Matters
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {[
              {
                icon: Sun,
                title: "Daytime Generation",
                desc: "Solar panels produce maximum energy during peak sunlight hours",
                color: "bg-amber-500",
              },
              {
                icon: Battery,
                title: "Energy Storage",
                desc: "Large-scale batteries store excess power for later use",
                color: "bg-[#002B7F]",
              },
              {
                icon: Home,
                title: "Nighttime Power",
                desc: "Stored energy keeps Nevada powered 24/7 — even after sunset",
                color: "bg-slate-800",
              },
            ].map((item, i) => {
              const Icon = item.icon;
              return (
                <div key={item.title} className="relative">
                  <div className="bg-white border border-slate-200 p-6 text-center h-full">
                    <div className={`w-12 h-12 mx-auto flex items-center justify-center ${item.color} text-white mb-4`}>
                      <Icon className="w-6 h-6" />
                    </div>
                    <h3 className="font-bold text-slate-900 mb-2">
                      {item.title}
                    </h3>
                    <p className="text-sm text-slate-600">{item.desc}</p>
                  </div>
                  {i < 2 && (
                    <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-10">
                      <ArrowRight className="w-6 h-6 text-[#D4AF37]" />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── GOVERNANCE MODEL ── */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6">
                Nevada-Owned Energy System
              </h2>
              <p className="text-slate-600 leading-relaxed mb-6">
                The state owns the generation and storage assets. Energy is sold
                at cost after financing. No profit markup, no shareholder
                obligations.
              </p>
              <div className="space-y-3">
                {[
                  "State owns all generation + storage assets",
                  "Energy sold at cost after financing",
                  "Revenue reinvested into rural infrastructure",
                  "Grid upgrades funded by system revenue",
                  "Community development investment in host regions",
                ].map((item) => (
                  <div key={item} className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Long-term vision */}
            <div className="bg-[#002B7F] p-8">
              <h3 className="text-xl font-bold text-white mb-6">
                A State Where Energy Costs Go Down Over Time
              </h3>
              <div className="space-y-4">
                {[
                  {
                    title: "Debt Repayment",
                    desc: "As financing is paid off, energy costs drop further — potentially below 5¢/kWh",
                  },
                  {
                    title: "Technology Improvement",
                    desc: "Solar efficiency continues to improve, producing more energy per panel",
                  },
                  {
                    title: "National Leadership",
                    desc: "Nevada becomes a model for state-owned clean energy nationwide",
                  },
                ].map((item, i) => (
                  <div
                    key={item.title}
                    className="bg-white/10 p-4 border border-white/20"
                  >
                    <p className="font-bold text-white text-sm mb-1">
                      {item.title}
                    </p>
                    <p className="text-white/70 text-sm">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="py-16 md:py-24 bg-[#F8F9FA]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4">
              Frequently Asked Questions
            </h2>
          </div>
          <div className="space-y-3">
            {faqs.map((faq) => (
              <FAQItem key={faq.question} question={faq.question} answer={faq.answer} />
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-16 md:py-24 bg-[#002B7F]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-6">
            A More Affordable Nevada Starts Here
          </h2>
          <p className="text-white/70 mb-8">
            Nevada should not be dependent on rising energy prices. We can
            produce our own power, for less.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/volunteer"
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#D4AF37] hover:bg-[#B5952F] text-black font-bold uppercase tracking-widest text-sm transition-all hover:-translate-y-1 hover:shadow-xl"
            >
              Support the Campaign
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-8 py-4 border-2 border-white text-white hover:bg-white hover:text-[#002B7F] font-bold uppercase tracking-widest text-sm transition-all"
            >
              Contact Us
            </Link>
            <Link
              to="/policies/tnv"
              className="inline-flex items-center gap-2 px-8 py-4 border-2 border-white/40 text-white/80 hover:border-white hover:text-white font-bold uppercase tracking-widest text-sm transition-all"
            >
              Traffic & Non-Violent (TNV) <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CleanEnergyPage;
